import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HomeIcon } from 'lucide-react';
import { useNotificationSearch } from '@/features/notifications/hooks/useNotificationSearch';
import { useAuth } from '@/features/auth/';
import { useNotificationUpdate } from '@/features/notifications/hooks/useNotificationUpdate';
import { toast } from 'sonner';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../ui/breadcrumb';
import { NotificationFilters, NotificationCard, notificationStatusOptions, type NotificationStatusValue } from '@/features/notifications/components';
import type { Notification } from '@/features/notifications/types/notification.types';

export const EstablishmentNotifications: React.FC = () => {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = React.useState<NotificationStatusValue>("abierta");
  const [openDialogId, setOpenDialogId] = React.useState<string | null>(null);
  const [observation, setObservation] = React.useState<string>("");

  const selectedStatus = notificationStatusOptions.find(opt => opt.value === statusFilter);
  const { notifications, isLoading, isError, error } = useNotificationSearch(user?.id, selectedStatus?.statusId);
  const { updateNotification, isLoading: isUpdating } = useNotificationUpdate();

  if (isLoading) {
    // return <div className="p-4">Cargando notificaciones...</div>;
  }
  
  if (isError) {
    return <div className="p-4 text-red-500">Error: {error instanceof Error ? error.message : 'Error al cargar notificaciones'}</div>;
  }

  const handleButtonClick = (notification: Notification) => {
    if (notification.notificationStatusId === 2) {
      updateNotification({
        id: notification.id,
        params: {
          ...notification,
          notificationStatusId: 3,
          respondedAt: new Date().toISOString(),
        }
      });
    } else if (notification.notificationStatusId === 1) {
      setOpenDialogId(notification.id);
      setObservation("");
    }
  };

  const handleUpdateWithObservation = (notification: Notification) => {
    updateNotification({
      id: notification.id,
      params: {
        ...notification,
        notificationStatusId: 2,
        respondedByUserId: user?.id,
        response: observation,
        respondedAt: new Date().toISOString(),
      }
    });
    setOpenDialogId(null);
    setObservation("");
    toast.success('Notificación actualizada correctamente');
  };

  const handleResolveClick = (notification: Notification) => {
    updateNotification({
      id: notification.id,
      params: {
        ...notification,
        notificationStatusId: 2,
        respondedByUserId: user?.id,
        response: "Ok, 👍",
        respondedAt: new Date().toISOString(),
      }
    });
    toast.success('Notificación actualizada correctamente');
  };

  return (
    <div className="space-y-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/">
              <HomeIcon className="h-4 w-4 mr-1" />
              <span>Inicio</span>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Notificaciones</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <h2 className="text-2xl font-bold mb-2">Notificaciones</h2>
      
      <NotificationFilters
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
      />
      
      {notifications.length === 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center">
            <div className="flex items-center">
              <CardTitle>Notificaciones</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-muted-foreground">
              No se han encontrado notificaciones para el filtro seleccionado.
            </div>
          </CardContent>
        </Card>
      )}
      
      {notifications.map((notification) => (
        <NotificationCard
          key={notification.id}
          notification={notification}
          userRole={user?.role || ''}
          isUpdating={isUpdating}
          openDialogId={openDialogId}
          observation={observation}
          onButtonClick={handleButtonClick}
          onResolveClick={handleResolveClick}
          onObservationChange={setObservation}
          onCloseDialog={() => setOpenDialogId(null)}
          onSubmitResponse={handleUpdateWithObservation}
        />
      ))}
    </div>
  );
};
